import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import {
  filter, map, switchMap, take,
} from 'rxjs/operators';
import { TypedApiService } from 'app/modules/websocket/typed-api/typed-api.service';
import { failoverLicensedStatusLoaded } from 'app/store/ha-info/ha-info.actions';
import { selectIsHaLicensed } from 'app/store/ha-info/ha-info.selectors';
import { rebootInfoLoaded, refreshRebootInfo } from 'app/store/reboot-info/reboot-info.actions';

@Injectable()
export class RebootInfoEffects {
  private actions$ = inject(Actions);
  private api = inject(TypedApiService);
  private store$ = inject(Store);

  // Latest wins here and below: the licensed status is seeded at sign-in and can be corrected once
  // the `HA` entitlement arrives, and a fetch started on the stale answer must not land after the
  // corrected one.
  loadRebootInfo = createEffect(() => this.actions$.pipe(
    ofType(failoverLicensedStatusLoaded),
    switchMap(({ isHaLicensed }) => this.fetchRebootInfo(isHaLicensed)),
  ));

  refreshRebootInfo = createEffect(() => this.actions$.pipe(
    ofType(refreshRebootInfo),
    switchMap(() => this.store$.select(selectIsHaLicensed).pipe(take(1))),
    switchMap((isHaLicensed) => this.fetchRebootInfo(isHaLicensed)),
  ));

  // The earlier subscription must be torn down on that correction too, or two reboot-info sources
  // race each other.
  subscribeToRebootInfo = createEffect(() => this.actions$.pipe(
    ofType(failoverLicensedStatusLoaded),
    switchMap(({ isHaLicensed }) => {
      if (isHaLicensed) {
        return this.api.subscribe('failover.reboot.info').pipe(
          filter((event) => event.msg === 'changed'),
          map((event) => rebootInfoLoaded({
            thisNodeRebootInfo: event.fields.this_node,
            otherNodeRebootInfo: event.fields.other_node,
          })),
        );
      }
      return this.api.subscribe('system.reboot.info').pipe(
        filter((event) => event.msg === 'changed'),
        map((event) => rebootInfoLoaded({
          thisNodeRebootInfo: event.fields,
          otherNodeRebootInfo: null,
        })),
      );
    }),
  ));

  private fetchRebootInfo(isHaLicensed: boolean): Observable<ReturnType<typeof rebootInfoLoaded>> {
    if (isHaLicensed) {
      return this.api.call('failover.reboot.info').pipe(
        map((info) => rebootInfoLoaded({
          thisNodeRebootInfo: info.this_node,
          otherNodeRebootInfo: info.other_node,
        })),
      );
    }
    return this.api.call('system.reboot.info').pipe(
      map((info) => rebootInfoLoaded({
        thisNodeRebootInfo: info,
        otherNodeRebootInfo: null,
      })),
    );
  }
}
